<template>
  <div>
    <!-- Modal Trigger Button -->
    <b-button @click="showModal = true" variant="primary" class="add-resource-btn">
      <feather-icon icon="PlusIcon" size="16" />
    </b-button>

    <!-- Create Customer Modal -->
    <b-modal
      v-model="showModal"
      title="Create New Customer"
      size="xl"
      :hide-footer="true"
      :hide-header="true"
      :hide-header-close="true"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      centered
      @hidden="onModalHidden"
      id="create-customer-modal"
      body-class="p-0 overflow-hidden rounded-md"
    >
      <!-- Modal Body -->
      <div class="modal-body p-0">
        <CustomerForm
          :form-data="customerFormData"
          :loading="loading"
          :modal-mode="true"
          @submit="handleSubmit"
          @update:form-data="updateFormData"
          @cancel="closeModal"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal, BButton, BSpinner } from 'bootstrap-vue';
import { ref, reactive } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import CustomerForm from './CustomerForm.vue';
import store from '@/store';
import CustomerStoreModule from './CustomerStoreModule';
import { generateRandomString } from '@/utils/functions';

export default {
  name: 'CreateCustomerModal',
  components: {
    BModal,
    BButton,
    BSpinner,
    FeatherIcon,
    CustomerForm,
    ToastificationContent,
  },
  emits: ['submit'],
  setup(props, { emit }) {
    const toast = useToast();
    const CUSTOMER_STORE_MODULE_NAME = 'customer';

    // Register module if not already registered
    if (!store.hasModule(CUSTOMER_STORE_MODULE_NAME)) {
      store.registerModule(CUSTOMER_STORE_MODULE_NAME, CustomerStoreModule);
    }

    // Reactive state
    const showModal = ref(false);
    const loading = ref(false);
    const customerFormData = reactive({
      id: 0,
      code: '',
      name: '',
      shortName: '',
      cellNumber: '',
      category: '',
      email: '',
      ntnNumber: '',
      gstNumber: '',
      address: '',
      province: '',
      cnicNumber: '',
      contactPerson: '',
      whatsappOrUAN: '',
      website: '',
      commissionPercent: 0,
      isActive: true,
    });

    // Methods
    const updateFormData = (newData) => {
      Object.assign(customerFormData, newData);
    };

    const handleSubmit = async () => {
      // Validate NTN or CNIC requirement
      if (!customerFormData.ntnNumber && !customerFormData.cnicNumber) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please enter either NTN or CNIC number',
            variant: 'warning',
            icon: 'AlertTriangleIcon',
          },
        });
        return;
      }

      loading.value = true;

      try {
        // Prepare data for API call (matching the store module expectations)
        const customerData = {
          code: generateRandomString(10),
          companyName: customerFormData.name, // Map name to companyName
          shortName: customerFormData.shortName,
          cellNo: customerFormData.cellNumber, // Map cellNumber to cellNo
          category: customerFormData.category,
          email: customerFormData.email,
          ntn: customerFormData.ntnNumber, // Map ntnNumber to ntn
          gstNo: customerFormData.gstNumber, // Map gstNumber to gstNo
          address: customerFormData.address,
          province: customerFormData.province,
          cnicNumber: customerFormData.cnicNumber,
          contactPerson: customerFormData.contactPerson,
          whatsapp: customerFormData.whatsappOrUAN, // Map whatsappOrUAN to whatsapp
          website: customerFormData.website,
          commissionPercent: customerFormData.commissionPercent,
          isActive: customerFormData.isActive,
        };

        // Call the store action to create customer
        const customerIdResponse = await store.dispatch('customer/addCustomer', customerData);
        console.log('=== CreateCustomerModal response:', customerIdResponse);

        // Show success message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Customer created successfully!',
            variant: 'success',
            icon: 'CheckIcon',
          },
        });

        // Reset form data
        resetFormData();

        // Close modal
        closeModal();

        // Emit submit event to parent
        emit('submit', customerIdResponse);
      } catch (error) {
        console.error('Error creating customer:', error);

        // Show error message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error?.response?.data?.message || 'Failed to create customer. Please try again.',
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        loading.value = false;
      }
    };

    const resetFormData = () => {
      Object.assign(customerFormData, {
        id: 0,
        code: '',
        name: '',
        shortName: '',
        cellNumber: '',
        category: '',
        email: '',
        ntnNumber: '',
        gstNumber: '',
        address: '',
        province: '',
        cnicNumber: '',
        contactPerson: '',
        whatsappOrUAN: '',
        website: '',
        commissionPercent: 0,
        isActive: true,
      });
    };

    const closeModal = () => {
      showModal.value = false;
    };

    const onModalHidden = () => {
      // Reset form when modal is hidden
      resetFormData();
    };

    return {
      // Reactive state
      showModal,
      loading,
      customerFormData,

      // Methods
      updateFormData,
      handleSubmit,
      resetFormData,
      closeModal,
      onModalHidden,
    };
  },
};
</script>

<style lang="scss" scoped>
.modal-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
}

::v-deep .modal-xl {
  max-width: 95%;
}

::v-deep .modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

::v-deep .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}
</style>
